import { useId, useRef, useState, type InputHTMLAttributes } from 'react'
import { Eye, EyeOff } from 'lucide-react'

export interface PasswordFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Classes for the `<input>` itself — the wrapper owns the button position. */
  inputClassName?: string
  /**
   * What this field holds, used to name the toggle for screen readers:
   * `revealLabel="new password"` becomes “Show new password”. Every password
   * field on a form gets its own name so two toggles are never ambiguous.
   */
  revealLabel?: string
  /** Test seam: start with the value visible (the smoke suite renders both states). */
  defaultRevealed?: boolean
}

/**
 * A password input with a show/hide toggle (D-107).
 *
 * Typing a long password blind on a phone keyboard is where sign-in attempts go to
 * die, and confirming a password you cannot read is worse. The toggle is a real
 * `button` (never a submit), it reports `aria-pressed`, its accessible name says
 * *which* password it reveals, and pressing it keeps the caret in the field so
 * revealing never costs you your place.
 *
 * The password still posts as a password string — this only changes what the screen
 * shows; nothing about the request or the backend changes.
 */
export function PasswordField({
  inputClassName = '',
  className = '',
  revealLabel = 'password',
  defaultRevealed = false,
  id,
  ...props
}: PasswordFieldProps) {
  const [revealed, setRevealed] = useState(defaultRevealed)
  const inputRef = useRef<HTMLInputElement>(null)
  const generatedId = useId()
  const fieldId = id ?? generatedId
  const action = revealed ? 'Hide' : 'Show'

  const toggle = () => {
    setRevealed((current) => !current)
    // Put the cursor back where it was; a mouse click on the toggle would
    // otherwise leave focus on the button mid-password.
    requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }))
  }

  return (
    <div className="relative w-full">
      <input
        {...props}
        id={fieldId}
        ref={inputRef}
        type={revealed ? 'text' : 'password'}
        // Revealed text is still a password: no autocapitalise, no autocorrect,
        // and no spellcheck squiggles under it.
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        className={`${className} ${inputClassName} pr-11`.trim()}
      />
      <button
        type="button"
        onClick={toggle}
        aria-pressed={revealed}
        aria-controls={fieldId}
        aria-label={`${action} ${revealLabel}`}
        title={`${action} ${revealLabel}`}
        className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-xl text-slate-400 transition-colors hover:text-slate-700 focus-visible:text-slate-900 dark:text-slate-500 dark:hover:text-slate-200"
      >
        {revealed ? (
          <EyeOff className="h-4 w-4" aria-hidden="true" />
        ) : (
          <Eye className="h-4 w-4" aria-hidden="true" />
        )}
      </button>
    </div>
  )
}

export default PasswordField
